import _jsx from "@babel/runtime/helpers/jsx";
var _Fragment, _Glyphicon;
import React, { Fragment, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useBrand } from '@atc/brand';
import { Button, Glyphicon, Link, List, SubHeading, Text } from 'reaxl';
import clsx from 'clsx';
const formatCondition = listingType => {
  if (!listingType) return '';
  return listingType.substr(0, 1).toUpperCase().concat(listingType.substr(1).toLowerCase());
};
const getCertifiedLabel = certifiedTiles => {
  if (!certifiedTiles?.length) return undefined;
  return certifiedTiles[0].label;
};
const getYMM = (year, make, model) => {
  if (!year || !make || !model) return '';
  return `${year} ${make} ${model}`;
};
const formatMileage = mileage => {
  if (!mileage) return undefined;
  const mileageInt = typeof mileage === 'number' ? mileage : parseInt(mileage.replace(',', ''), 10);
  return mileageInt < 1000 ? `${mileageInt} mi` : `${Math.floor(mileageInt / 1000)}K mi`;
};
const getFuelType = (fuelType, engine, fuelGroup) => {
  if (fuelType === 'Hybrid Gas/Electric' && engine === 'Plug-in Hybrid' || fuelGroup === 'Plug-in Hybrid: Gas/Electric' && engine === 'Hybrid') {
    return {
      label: 'Plug-In Hybrid',
      glyph: 'plugin-hybrid'
    };
  }
  if (fuelType === 'Hybrid Gas/Electric' && engine === 'Gas / Electric Hybrid' || fuelGroup === 'Hybrid: Gas/Electric' && engine === 'Hybrid') {
    return {
      label: 'Hybrid',
      glyph: 'hybrid'
    };
  }
  if (fuelType === 'Electric' && engine === 'Electric' || fuelGroup === 'Electric' && engine === 'Electric') {
    return {
      label: 'Electric',
      glyph: 'electric'
    };
  }
  return {
    label: null,
    glyph: null
  };
};

/**
 *  Renders a Listing Info element
 *
 */
function InventoryListingInfo(_ref) {
  let {
    year,
    make,
    model,
    trim,
    mileage,
    engine,
    fuelType,
    fuelGroup,
    listingType,
    certifiedTiles,
    onCertifiedTileClick,
    uiContext,
    offsite = false,
    ...rest
  } = _ref;
  const condition = useMemo(() => formatCondition(listingType), [listingType]);
  const certified = useMemo(() => getCertifiedLabel(certifiedTiles), [certifiedTiles]);
  const ymm = useMemo(() => getYMM(year, make, model), [year, make, model]);
  const mileageFormatted = useMemo(() => formatMileage(mileage), [mileage]);
  const fuelInfo = useMemo(() => getFuelType(fuelType, engine, fuelGroup), [fuelType, engine, fuelGroup]);
  const {
    isBrand
  } = useBrand();
  const isFord = isBrand('ford');
  const conditionElement = certified && !isFord ? /*#__PURE__*/_jsx(Button, {
    "data-cmp": "certifiedButton",
    className: "condition",
    bsStyle: "text",
    onClick: onCertifiedTileClick
  }, "listing-certified-button", condition === 'Certified' && (_Fragment || (_Fragment = /*#__PURE__*/_jsx(Fragment, {}, "certified-icon", /*#__PURE__*/_jsx(Glyphicon, {
    glyph: "certified"
  }), "\xA0"))), certified) : /*#__PURE__*/_jsx(Text, {
    "data-cmp": "listingCondition",
    className: "condition"
  }, "listing-condition-text", condition);
  const subheadingElement = /*#__PURE__*/_jsx(SubHeading, {
    "data-cmp": "subheading",
    componentClass: "h2",
    size: 400,
    className: clsx({
      'text-overflow padding-vertical-1': true,
      'link-unstyled': rest.href
    })
  }, "listing-subheading", ymm, offsite && (_Glyphicon || (_Glyphicon = /*#__PURE__*/_jsx(Glyphicon, {
    className: "margin-left-1",
    glyph: "offsite",
    "aria-label": "External Link"
  }, "offsite-icon"))));
  const renderSpecifications = item => {
    if (item?.className) {
      return /*#__PURE__*/_jsx("span", {
        className: item.className
      }, void 0, item.value);
    }
    return item?.value;
  };
  const specificationsElement = /*#__PURE__*/_jsx("div", {
    "data-cmp": "listingSpecifications",
    className: "specifications"
  }, "listing-specifications", (trim || mileageFormatted) && /*#__PURE__*/_jsx(List, {
    inline: true,
    items: [...(trim ? [{
      key: 'trim',
      className: 'text-overflow',
      value: trim
    }] : []), ...(mileageFormatted ? [{
      key: 'mileage',
      value: mileageFormatted
    }] : [])],
    render: renderSpecifications
  }, "specifications-list"), fuelInfo?.label && /*#__PURE__*/_jsx("span", {
    className: "fuel-type"
  }, "fuel-type", /*#__PURE__*/_jsx(Glyphicon, {
    glyph: fuelInfo.glyph
  }), "\xA0", fuelInfo.label));
  const linkProps = {
    ...rest,
    key: 'listing-ymm-link',
    undecorated: true
  };

  // If brand=ford, then do not use rel=sponsored,nofollow for SEO purposes
  if (!isFord && uiContext === 'sponsored') {
    linkProps.rel = 'sponsored,nofollow';
  }
  const renderSubheadingElement = () => {
    if (!rest.href) {
      return subheadingElement;
    }
    return /*#__PURE__*/React.createElement(Link, linkProps, subheadingElement);
  };
  return /*#__PURE__*/_jsx("div", {
    "data-cmp": "listing-title-info",
    className: "title-info"
  }, "listing-title-info", uiContext !== 'recommended' && conditionElement, renderSubheadingElement(), specificationsElement);
}
export default /*#__PURE__*/React.memo(InventoryListingInfo);